import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { RegisterSplitLayout } from '../../components/register/RegisterSplitLayout';
import { REGISTER_VISUALS } from '../../components/register/registerVisuals';
import { useRegistrationOnboardingGuard } from '../../lib/hooks/useRegistrationOnboardingGuard';
import { buildMicrosoftAuthUrl } from '../../lib/microsoft/microsoftScopes';
import { trackRegisterStepViewed } from '../../lib/analytics/onboardingEvents';

/**
 * Step 7: "Porta le tue relazioni in Flusia" — collega Google Workspace /
 * Microsoft 365 riusando i flussi OAuth delle Impostazioni, con ritorno su
 * questa pagina (state.redirectTo). Interamente skippabile.
 */
export function ConnectPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ready, orgId } = useRegistrationOnboardingGuard();
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);
  const [launching, setLaunching] = useState<'google' | 'microsoft' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Ritorno dal provider: GoogleAuthCallback appende openModal=google al redirectTo.
  // `connected` è una LISTA separata da virgole: collegando il secondo provider il
  // redirect deve portarsi dietro il primo, altrimenti la sua spunta sparisce
  // (bug visto il 14/08: Microsoft poi Google → spunta Microsoft persa).
  const connectedList = (searchParams.get('connected') || '').split(',').filter(Boolean);
  const connectedGoogle = connectedList.includes('google') || searchParams.get('openModal') === 'google';
  const connectedMicrosoft = connectedList.includes('microsoft');
  // Lista da propagare nel redirectTo del prossimo OAuth (incluso il provider nuovo).
  const connectedWith = (provider: 'google' | 'microsoft') =>
    [...new Set([
      ...connectedList,
      ...(connectedGoogle ? ['google'] : []),
      ...(connectedMicrosoft ? ['microsoft'] : []),
      provider,
    ])].join(',');
  const microsoftAvailable = Boolean(import.meta.env.VITE_MICROSOFT_CLIENT_ID);

  useEffect(() => { trackRegisterStepViewed('connect'); }, []);

  useEffect(() => {
    if (!orgId) return;
    supabase
      .from('settings')
      .select('google_workspace_client_id')
      .eq('organization_id', orgId)
      .single()
      .then(({ data }) => setGoogleClientId(data?.google_workspace_client_id || null));
  }, [orgId]);

  const goNext = () => navigate('/register/modules');

  const connectGoogle = () => {
    if (!googleClientId) return;
    setLaunching('google');
    // Stesso authorize URL di GoogleWorkspaceSettings, scope minimi per i contatti
    // e il calendario; consenso incrementale per il resto dalle Impostazioni.
    const scopes = [
      'https://www.googleapis.com/auth/userinfo.email',
      'https://www.googleapis.com/auth/userinfo.profile',
      'https://www.googleapis.com/auth/calendar',
      'https://www.googleapis.com/auth/contacts.readonly',
    ];
    const state = encodeURIComponent(JSON.stringify({ redirectTo: `/register/connect?connected=${connectedWith('google')}` }));
    const oauthUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    oauthUrl.searchParams.append('client_id', googleClientId);
    oauthUrl.searchParams.append('redirect_uri', 'https://app.flusia.it/digital-google-connect');
    oauthUrl.searchParams.append('response_type', 'code');
    oauthUrl.searchParams.append('scope', scopes.join(' '));
    oauthUrl.searchParams.append('access_type', 'offline');
    oauthUrl.searchParams.append('prompt', 'consent');
    oauthUrl.searchParams.append('state', state);
    window.location.href = oauthUrl.toString();
  };

  const connectMicrosoft = async () => {
    setLaunching('microsoft');
    setError(null);
    try {
      const url = await buildMicrosoftAuthUrl({
        services: ['calendar', 'contacts'],
        redirectTo: `/register/connect?connected=${connectedWith('microsoft')}`,
        prompt: 'consent',
      });
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Errore nell'avvio dell'autorizzazione Microsoft");
      setLaunching(null);
    }
  };

  if (!ready) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#787774]" />
      </div>
    );
  }

  const providerBtn = 'w-full flex items-center justify-between gap-3 py-3.5 px-4 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white hover:bg-[rgba(55,53,47,0.03)] text-[#37352F] text-sm font-medium transition-all disabled:opacity-50';

  return (
    <RegisterSplitLayout visual={REGISTER_VISUALS.connect} step={7}>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#37352F] mb-1">Porta le tue relazioni in Flusia</h1>
        <p className="text-[#787774] text-sm">Collega calendario e contatti — potrai gestire tutto dalle Impostazioni</p>
      </div>
      <div className="space-y-3">
        {googleClientId && (
          <button type="button" onClick={connectGoogle} disabled={!!launching || connectedGoogle} className={providerBtn}>
            <span className="flex items-center gap-3">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Collega Google Workspace
            </span>
            {connectedGoogle ? <Check className="w-4 h-4 text-[#0F7B6C]" /> :
              launching === 'google' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          </button>
        )}
        {microsoftAvailable && (
          <button type="button" onClick={connectMicrosoft} disabled={!!launching || connectedMicrosoft} className={providerBtn}>
            <span className="flex items-center gap-3">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#F25022" d="M1 1h10v10H1z"/><path fill="#00A4EF" d="M1 13h10v10H1z"/>
                <path fill="#7FBA00" d="M13 1h10v10H13z"/><path fill="#FFB900" d="M13 13h10v10H13z"/>
              </svg>
              Collega Microsoft 365
            </span>
            {connectedMicrosoft ? <Check className="w-4 h-4 text-[#0F7B6C]" /> :
              launching === 'microsoft' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          </button>
        )}
      </div>
      {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
      <div className="mt-8 space-y-3">
        <button type="button" onClick={goNext} className="notion-register-btn w-full">
          <span>Continua</span>
          <ArrowRight className="w-4 h-4" />
        </button>
        <button type="button" onClick={goNext}
          className="w-full text-center text-sm text-[#787774] hover:text-[#37352F] transition-colors py-1.5">
          Creo i contatti manualmente
        </button>
      </div>
    </RegisterSplitLayout>
  );
}
